import { createContext } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { DashboardViewKey } from '../types/schema'
import type { WorkspaceLimits } from '../types/schema'
import type { ChecklistInputLimits } from '../utils/checklistLimits'
import type { CreateWorkspaceOptions, Workspace, WorkspaceKind, WorkspaceRole } from '../types/workspace'
import type { WorkspaceAction } from '../utils/workspacePermissions'

export interface WorkspaceContextValue {
  workspaces: Workspace[]
  activeWorkspaceId: string
  activeRole: WorkspaceRole
  rolesByWorkspaceId?: Map<string, WorkspaceRole>
  canPerform: (action: WorkspaceAction) => boolean
  canPerformByRole: (action: WorkspaceAction) => boolean
  getWorkspaceRole: (workspaceId: string) => WorkspaceRole
  appMode: WorkspaceKind
  currentUserCredits: number
  isWorkspaceLoading: boolean
  needsOnboarding: boolean
  workspaceLoadingError?: string | null
  activeMemberDefaultView: DashboardViewKey | null
  isSidebarPinned: boolean
  setSidebarPinned: Dispatch<SetStateAction<boolean>>
  setActiveWorkspaceId: (workspaceId: string) => void
  openPreviewWorkspace: (workspace: Workspace) => void
  /** Fetch the public mirror doc for a workspace and mount it as a synthetic
   * READONLY preview so the visitor can walk through the surfaces the Admin
   * allowed via `previewSurfaceMask`. */
  openPublicWorkspacePreview: (input: { workspaceId: string; slug: string }) => Promise<void>
  setAppMode: (mode: WorkspaceKind) => void
  createWorkspace: (name: string, kind: WorkspaceKind, options?: boolean | CreateWorkspaceOptions) => Promise<Workspace>
  checkWorkspaceSlugAvailability: (slug: string) => Promise<{ available: boolean; error: string | null }>
  removeWorkspaceFromState: (workspaceId: string) => void
  refreshWorkspaces: (
    preferredWorkspaceId?: string,
    preferredMode?: WorkspaceKind,
    options?: { quiet?: boolean },
  ) => Promise<Workspace[] | void>
  setMemberDefaultDashboardView: (workspaceId: string, view: DashboardViewKey) => Promise<void>
  setWorkspaceDashboardViewConfig: (
    workspaceId: string,
    patch: { enabledViews?: DashboardViewKey[]; defaultView?: DashboardViewKey },
  ) => Promise<void>
  setWorkspaceChecklistLimit: (
    workspaceId: string,
    field: keyof ChecklistInputLimits,
    value: number,
  ) => Promise<number>
  setWorkspaceLimits: (workspaceId: string, workspaceLimits: WorkspaceLimits) => Promise<void>
}

export const ACTIVE_WORKSPACE_STORAGE_KEY = 'timelining-active-workspace'
export const ACTIVE_WORKSPACE_TAB_STORAGE_KEY = 'timelining-active-workspace:tab'
export const APP_MODE_STORAGE_KEY = 'timelining-app-mode'

export const clearWorkspaceStorage = () => {
  window.localStorage.removeItem('activeWorkspaceId')
  window.localStorage.removeItem('appMode')
  window.localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY)
  window.localStorage.removeItem(APP_MODE_STORAGE_KEY)
  try {
    window.sessionStorage.removeItem(ACTIVE_WORKSPACE_TAB_STORAGE_KEY)
  } catch {
    // ignore
  }
  Object.keys(window.localStorage)
    .filter((key) => key.toLowerCase().includes('workspace'))
    .forEach((key) => window.localStorage.removeItem(key))
}

export const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined)
